import axios from 'axios';
import { API_BASE_URL } from './config';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Subscription interface
export interface Subscription {
  id: string;
  planName: string;
  planType: 'basic' | 'professional' | 'enterprise' | 'custom';
  status: 'active' | 'cancelled' | 'expired' | 'trial' | 'suspended';
  customerName: string;
  customerEmail: string;
  customerType: 'individual' | 'business';
  startDate: string;
  endDate: string;
  nextBillingDate: string;
  billingCycle: 'monthly' | 'quarterly' | 'yearly';
  amount: number;
  currency: string;
  features: string[];
  userCount: number;
  maxUsers: number;
  storageUsed: number;
  maxStorage: number;
  paymentMethod: string;
  lastPayment: string;
  totalRevenue: number;
  discountApplied?: {
    code: string;
    percentage: number;
    amount: number;
  };
}

// Plan interface
export interface Plan {
  id: string;
  name: string;
  type: 'basic' | 'professional' | 'enterprise' | 'custom';
  description: string;
  price: {
    monthly: number;
    yearly: number;
  };
  features: string[];
  maxUsers: number;
  maxStorage: number;
  support: string;
  popular?: boolean;
}

// Subscription filters interface
export interface SubscriptionFilters {
  status?: string;
  planType?: string;
  customerType?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Subscription statistics interface
export interface SubscriptionStats {
  total: number;
  active: number;
  trial: number;
  cancelled: number;
  expired: number;
  autoRenewEnabled: number;
  byPlan: Array<{
    planName: string;
    count: number;
    activeCount: number;
  }>;
  byBillingCycle: Array<{
    _id: string;
    count: number;
    activeCount: number;
  }>;
  churnRate: number;
}

class SubscriptionService {
  // Get all subscriptions
  async getSubscriptions(filters?: SubscriptionFilters): Promise<{
    subscriptions: Subscription[];
    totalPages: number;
    currentPage: number;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/subscriptions?${params.toString()}`);
    return response.data;
  }

  // Get subscription by ID
  async getSubscription(id: string): Promise<Subscription> {
    const response = await api.get(`/subscriptions/${id}`);
    return response.data;
  }

  // Create new subscription
  async createSubscription(subscriptionData: Partial<Subscription>): Promise<Subscription> {
    const response = await api.post('/subscriptions', subscriptionData);
    return response.data;
  }

  // Update subscription
  async updateSubscription(id: string, updates: Partial<Subscription>): Promise<Subscription> {
    const response = await api.put(`/subscriptions/${id}`, updates);
    return response.data;
  }

  // Cancel subscription
  async cancelSubscription(id: string, reason?: string): Promise<Subscription> {
    const response = await api.patch(`/subscriptions/${id}/cancel`, { reason });
    return response.data;
  }

  // Reactivate subscription
  async reactivateSubscription(id: string): Promise<Subscription> {
    const response = await api.patch(`/subscriptions/${id}/reactivate`);
    return response.data;
  }

  // Change subscription plan
  async changeSubscriptionPlan(id: string, newPlan: string, reason?: string): Promise<Subscription> {
    const response = await api.patch(`/subscriptions/${id}/change-plan`, { newPlan, reason });
    return response.data;
  }

  // Renew subscription
  async renewSubscription(id: string): Promise<Subscription> {
    const response = await api.post(`/subscriptions/${id}/renew`);
    return response.data;
  }

  // Get subscription usage/statistics
  async getSubscriptionUsage(id: string): Promise<any> {
    const response = await api.get(`/subscriptions/${id}/usage`);
    return response.data;
  }

  // Delete subscription
  async deleteSubscription(id: string): Promise<void> {
    await api.delete(`/subscriptions/${id}`);
  }

  // Get subscription statistics
  async getSubscriptionStats(filters?: SubscriptionFilters): Promise<SubscriptionStats> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/subscriptions/stats?${params.toString()}`);
    return response.data;
  }

  // Get available plans
  async getPlans(): Promise<Plan[]> {
    const response = await api.get('/plans');
    return response.data;
  }

  // Get plan by ID
  async getPlan(id: string): Promise<Plan> {
    const response = await api.get(`/plans/${id}`);
    return response.data;
  }

  // Search subscriptions
  async searchSubscriptions(query: string, filters?: SubscriptionFilters): Promise<Subscription[]> {
    const params = new URLSearchParams({ search: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'search') {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/subscriptions/search?${params.toString()}`);
    return response.data;
  }

  // Bulk operations
  async bulkUpdateSubscriptions(ids: string[], updates: Partial<Subscription>): Promise<void> {
    await api.patch('/subscriptions/bulk', { ids, updates });
  }

  async bulkDeleteSubscriptions(ids: string[]): Promise<void> {
    await api.delete('/subscriptions/bulk', { data: { ids } });
  }

  // Export subscriptions
  async exportSubscriptions(filters?: SubscriptionFilters, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/subscriptions/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;